import { useRef, useState, type ReactNode, useEffect } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShellApp, type EnlaceProps, type SeccionNavegacion } from '@xiltrion/ui/bloques/shell-app';
import { CentroNotificaciones } from '@xiltrion/ui/bloques/centro-notificaciones';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { EstadoCargando } from '@xiltrion/ui/bloques/estados';
import { Alert, AlertAction, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Icono } from '@xiltrion/ui/icono';
import { Item, ItemContent, ItemDescription, ItemTitle } from '@xiltrion/ui/item';
import { apiGet, apiPost } from '@/api/client';
import type { NotificationRow } from '@/api/types';
import { appColor } from './app-color';
import { appLogo } from './app-logo';
import { useSession } from './session';
import { getThemeMode, setThemeMode } from './theme';
import { useNotificationsStream } from './realtime';

const Enlace = ({ href, className, children, onClick, ...rest }: EnlaceProps) => <Link to={href} className={className} onClick={onClick} aria-current={rest['aria-current']}>{children}</Link>;

/** Armazón del portal sobre `ShellApp`: selector de apps de la entidad activa, selector de entidad, campana y menú. */
export function AppShell() {
  const { me, loading, superadmin, entity, setEntity, logout } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pickEntity, setPickEntity] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  // Al salir no se recuerda la página: quien ingrese después empieza en el Inicio (y salta a su única app).
  // Al salir, la portada recibe la entidad activa en la URL (`?entidad=`); igual cuando la sesión vence estando
  // dentro. Quien llega sin sesión alguna no lleva entidad: ve la portada sin marca (la del superadministrador).
  const leaving = useRef<string | null>(null);
  const hadEntity = useRef<string | null>(null);
  useEffect(() => { if (entity) hadEntity.current = entity.slug; }, [entity]);
  const { data: notifications = [] } = useQuery({ queryKey: ['notifications', 'recent'], queryFn: () => apiGet<NotificationRow[]>('/me/notifications', { limit: 20 }), enabled: !!me, refetchInterval: 120_000 });
  useNotificationsStream(!!me);
  if (loading) return <div className="p-10"><EstadoCargando /></div>;
  if (!me) {
    const entidad = leaving.current ?? hadEntity.current;
    const con = entidad ? `&entidad=${encodeURIComponent(entidad)}` : '';
    return <Navigate to={leaving.current !== null ? `/ingreso?${con.slice(1)}` : `/ingreso?volver=${encodeURIComponent(location.pathname + location.search)}${con}`} replace />;
  }

  const unread = notifications.filter((n) => !n.readAt).length;
  // El Inicio va sin barra lateral cuando hay una entidad activa con apps: las tarjetas de «Mi día» son el selector de
  // apps; lo que la barra daba (cambiar de entidad, la consola de Datalog) pasa a la barra superior, y Avisos y Mi perfil
  // siguen en la campana y el menú. Sin entidad (el superadmin, o quien aún no pertenece a ninguna) no hay tarjetas que
  // la reemplacen, así que la barra se queda.
  const home = location.pathname === '/' && !!entity?.apps.length;
  // El lanzador del ShellApp: solo las apps de la entidad activa, con su color, descripción y logo (la suite no es una
  // ficha: ya se está en ella; a Inicio se vuelve por la barra lateral).
  const apps = (entity?.apps ?? []).map((a) => ({ id: a.code, nombre: a.name, icono: a.icon as never, href: `${a.webUrl.replace(/\/$/, '')}/?tenant=${encodeURIComponent(entity!.slug)}`, color: appColor(a), descripcion: a.description, logo: appLogo(a.code) }));
  // «Panel de control» al pie del lanzador: solo para el ADMIN de la entidad activa (el super entra por Datalog › Entidades).
  const panelControl = !superadmin && entity?.role === 'ADMIN' ? `/entidades/${entity.slug}` : undefined;
  const navegacion: SeccionNavegacion[] = [
    { id: 'main', items: [{ id: 'home', etiqueta: 'Inicio', icono: 'mdi:home-outline', href: '/' }, { id: 'notifications', etiqueta: 'Avisos', icono: 'mdi:bell-outline', href: '/avisos' }, { id: 'profile', etiqueta: 'Mi perfil', icono: 'mdi:account-circle-outline', href: '/perfil' }] },
    // El super no pertenece a entidades: las abre desde Datalog › Entidades.
    ...(!superadmin && me.entities.some((e) => e.role === 'ADMIN')
      ? [{ id: 'entities', titulo: 'Mis entidades', items: me.entities.filter((e) => e.role === 'ADMIN').map((e) => ({ id: e.slug, etiqueta: e.name, icono: 'mdi:domain' as const, href: `/entidades/${e.slug}` })) }]
      : []),
    ...(superadmin
      ? [{ id: 'admin', titulo: 'Datalog', items: [
          { id: 'a-entities', etiqueta: 'Entidades', icono: 'mdi:office-building-outline' as const, href: '/admin/entidades' },
          { id: 'a-apps', etiqueta: 'Apps', icono: 'mdi:apps' as const, href: '/admin/apps' },
          { id: 'a-users', etiqueta: 'Personas', icono: 'mdi:account-group-outline' as const, href: '/admin/usuarios' },
          { id: 'a-mail', etiqueta: 'Correo', icono: 'mdi:email-outline' as const, href: '/admin/correo' },
          { id: 'a-backups', etiqueta: 'Respaldos', icono: 'mdi:backup-restore' as const, href: '/admin/respaldos' },
          { id: 'a-audit', etiqueta: 'Auditoría', icono: 'mdi:history' as const, href: '/admin/auditoria' },
        ] }]
      : []),
  ];
  const franja: ReactNode = me.mfaSetupRequired ? (
    <Alert variant="warning" className="rounded-none border-x-0 border-t-0"><Icono nombre="mdi:alert-outline" /><AlertDescription>Activa la verificación en dos pasos para poder hacer cambios.</AlertDescription><AlertAction><Button size="sm" onClick={() => navigate('/perfil')}>Activar</Button></AlertAction></Alert>
  ) : null;
  return (
    <ShellApp
      nombreApp="Suite"
      nombreSuite="Suite Datalog"
      navegacion={navegacion}
      rutaActual={location.pathname}
      Enlace={Enlace}
      apps={apps}
      panelControl={panelControl}
      entidad={entity ? { nombre: entity.name, logo: entity.branding.logoUrl ?? undefined, onCambiar: me.entities.length > 1 ? () => setPickEntity(true) : undefined } : undefined}
      usuario={{ nombre: me.displayName, correo: me.email, color: me.avatarColor }}
      franja={franja}
      vistaSuperadmin={superadmin}
      sinBarraLateral={home}
      barraSuperior={home ? (
        <>
          {superadmin && <Button variant="ghost" size="sm" asChild><Link to="/admin/entidades"><Icono nombre="mdi:shield-crown-outline" />Datalog</Link></Button>}
          {me.entities.length > 1 && <Button variant="ghost" size="sm" onClick={() => setPickEntity(true)}><Icono nombre="mdi:swap-horizontal" />Cambiar de entidad</Button>}
        </>
      ) : undefined}
      avisos={<CentroNotificaciones noLeidos={unread} avisos={notifications.map((n) => ({ id: n.id, titulo: n.title, detalle: n.appName === 'Suite' ? n.body : `${n.appName}${n.body ? ` · ${n.body}` : ''}`, fecha: n.createdAt, leido: !!n.readAt, icono: n.appIcon as never }))} onAbrir={(a) => { const n = notifications.find((x) => x.id === a.id); void apiPost('/me/notifications/read', { ids: [a.id] }).then(() => qc.invalidateQueries({ queryKey: ['notifications'] })); if (n?.link) window.location.href = n.link; }} onMarcarLeidos={(ids) => void apiPost('/me/notifications/read', { ids: ids === 'todos' ? 'all' : ids }).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }))} onVerTodos={() => navigate('/avisos')} />}
      version={__APP_VERSION__}
      menuUsuario={[
        { etiqueta: 'Mi perfil', icono: 'mdi:account-circle-outline', onSelect: () => navigate('/perfil') },
        { etiqueta: getThemeMode() === 'dark' ? 'Modo claro' : 'Modo oscuro', icono: 'mdi:theme-light-dark', onSelect: () => setThemeMode(getThemeMode() === 'dark' ? 'light' : 'dark') },
        { etiqueta: 'Cerrar sesión', icono: 'mdi:logout', destructivo: true, onSelect: () => setConfirmLogout(true) },
      ]}
    >
      <Outlet />
      {/* El mismo «Cerrar sesión» de las apps (GUIA §3.1): cierra el portal, el SSO y la sesión de cada app. */}
      <ConfirmarDialogo abierto={confirmLogout} onAbiertoChange={setConfirmLogout} tamano="sm" tono="warning" icono="mdi:logout" titulo="¿Está seguro?" descripcion="Se cerrará su sesión en todas las apps de la suite Datalog." textoConfirmar="Cerrar sesión" onConfirmar={async () => { leaving.current = entity?.slug ?? ''; await logout(); }} />
      <Dialog open={pickEntity} onOpenChange={setPickEntity}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambiar de entidad</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {me.entities.map((e) => (
              <Item key={e.slug} variant="outline" asChild>
                <button type="button" className="w-full text-left" onClick={() => { setEntity(e.slug); setPickEntity(false); }}>
                  <Icono nombre="mdi:domain" className="size-5 text-muted-foreground" />
                  <ItemContent><ItemTitle>{e.name}</ItemTitle><ItemDescription>{e.role === 'ADMIN' ? 'Administrador' : 'Miembro'} · {e.apps.length} apps</ItemDescription></ItemContent>
                  {entity?.slug === e.slug && <Icono nombre="mdi:check" />}
                </button>
              </Item>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </ShellApp>
  );
}
